/* ============================================================
   HOTH Fee Calculator
   Data (campuses, schedules, electives, bus, insurance, subsidy)
   is fetched from hoth-school-fees.json — see the section's root
   element data-json-url attribute for the asset URL.

   JSON shape (new):
   {
     "defaultYear": "2026",
     "rates": {
       "2026": { gstRate, registration, insurance, uniform, busNote,
                 subsidyLabels, campuses, rateNote? },
       "2027": { ... }
     }
   }
   The old flat shape (single year, no "rates" key) still works;
   the year dropdown is hidden in that case.
   ============================================================ */
(function () {
  const root = document.getElementById("hoth-fee-page-root");
  if (!root) return;

  // Optional: set data-campus="balmoral|hollandse|mountsophia|pasirpanjang"
  // on the root element to lock the calculator to a single campus.
  const LOCKED_CAMPUS = (root.dataset.campus || "").trim();
  // Optional: data-age-groups="infant" or "playgroup,nursery-k" limits which
  // age groups are offered for the locked campus (used to split Balmoral
  // into Childcare and Nido). A single remaining age group hides the field.
  const ALLOWED_AGE_GROUPS = (root.dataset.ageGroups || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  // Optional: data-campus-label overrides the campus name in the summary.
  const CAMPUS_LABEL = (root.dataset.campusLabel || "").trim();

  const jsonUrl = root.dataset.jsonUrl;
  if (!jsonUrl) {
    console.error(
      "HOTH Fee Calculator: missing data-json-url on #hoth-fee-page-root"
    );
    return;
  }

  fetch(jsonUrl)
    .then((res) => {
      if (!res.ok)
        throw new Error("Failed to load fee data (" + res.status + ")");
      return res.json();
    })
    .then(init)
    .catch((err) => {
      console.error("HOTH Fee Calculator:", err);
      const body = document.getElementById("summaryBody");
      if (body) {
        body.innerHTML =
          '<p class="hoth-summary__placeholder">Fee data could not be loaded. Please refresh, or contact the site admin if this persists.</p>';
      }
    });

  function init(RAW) {
    /* ---- year-aware data ---- */
    const RATES = RAW.rates || { current: RAW };
    const YEARS = Object.keys(RATES);
    let YEAR =
      RAW.defaultYear && RATES[RAW.defaultYear] ? RAW.defaultYear : YEARS[0];

    let GST_RATE,
      INSURANCE,
      REGISTRATION,
      UNIFORM,
      BUS_NOTE,
      CAMPUSES,
      SUBSIDY_LABELS,
      YEAR_NOTE;

    function loadYear(year) {
      const D = RATES[year];
      YEAR = year;
      GST_RATE = D.gstRate || 0.09;
      INSURANCE = D.insurance;
      REGISTRATION = D.registration;
      UNIFORM = D.uniform;
      BUS_NOTE = D.busNote;
      CAMPUSES = D.campuses;
      SUBSIDY_LABELS = D.subsidyLabels || {};
      YEAR_NOTE = D.rateNote || "";
    }
    loadYear(YEAR);

    const fmt = (n) => {
      const sign = n < 0 ? "-" : "";
      return (
        sign +
        "S$" +
        Math.abs(n).toLocaleString("en-SG", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    };

    /* ---- DOM refs ---- */
    const el = (id) => document.getElementById(id);
    const yearSel = el("rateYear"),
      yearField = el("yearField"),
      yearHint = el("rateYearHint");
    const campusSel = el("campus"),
      ageSel = el("ageGroup"),
      schedSel = el("schedule");
    const ageGroupField = el("ageGroupField"),
      ageGroupHint = el("ageGroupHint");
    const memberField = el("memberField"),
      hcMember = el("hcMember");
    const citizenshipField = el("citizenshipField"),
      citizenshipSel = el("citizenshipStatus");
    const subsidyField = el("subsidyField"),
      subsidyStatusSel = el("subsidyStatus"),
      subsidyHint = el("subsidyHint");
    const busNoticeWrap = el("busNoticeWrap");
    const busNoticeText = el("busNoticeText");
    const additionalServicesGroup = el("additionalServicesGroup");
    const electivesList = el("electivesList");

    /* ---- build static parts ---- */
    function populateYears() {
      if (!yearSel) return;
      yearSel.innerHTML = "";
      YEARS.forEach((y) => {
        const o = document.createElement("option");
        o.value = y;
        o.textContent = y === "current" ? "Current" : y;
        yearSel.appendChild(o);
      });
      yearSel.value = YEAR;
      if (yearField) yearField.hidden = YEARS.length <= 1;
      if (yearHint) yearHint.textContent = YEAR_NOTE;
    }

    function populateCampuses() {
      // If a campus is locked (and exists in this year's data), show only
      // that one and hide the dropdown. Otherwise list every campus.
      const keys =
        LOCKED_CAMPUS && CAMPUSES[LOCKED_CAMPUS]
          ? [LOCKED_CAMPUS]
          : Object.keys(CAMPUSES);
      campusSel.innerHTML = "";
      keys.forEach((key) => {
        const o = document.createElement("option");
        o.value = key;
        o.textContent = CAMPUSES[key].name;
        campusSel.appendChild(o);
      });
      const campusField = campusSel.closest(".hoth-field");
      if (campusField) campusField.hidden = keys.length <= 1;
    }

    const ICON_ADDRESS = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip_pin_centre_TynbJ9)">
        <path d="M13.3346 6.66658C13.3346 9.61212 8.0013 14.6666 8.0013 14.6666C8.0013 14.6666 2.66797 9.61212 2.66797 6.66658C2.66797 3.72107 5.05578 1.33325 8.0013 1.33325C10.9468 1.33325 13.3346 3.72107 13.3346 6.66658Z" stroke="currentColor" stroke-width="1.5"></path>
        <path d="M7.9987 7.33333C8.3669 7.33333 8.66536 7.03487 8.66536 6.66667C8.66536 6.29848 8.3669 6 7.9987 6C7.6305 6 7.33203 6.29848 7.33203 6.66667C7.33203 7.03487 7.6305 7.33333 7.9987 7.33333Z" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
      </g>
      <defs>
        <clipPath id="clip_pin_centre_TynbJ9">
          <rect width="16" height="16" fill="white"></rect>
        </clipPath>
      </defs>
    </svg>`;

    const ICON_PHONE = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    const ICON_EMAIL = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="m22 6-10 7L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    function contactRow(icon, innerHtml) {
      return `<div class="hoth-contact-row">${icon}<span>${innerHtml}</span></div>`;
    }

    function updateAdditionalServicesGroup() {
      const c = CAMPUSES[campusSel.value];
      if (additionalServicesGroup) additionalServicesGroup.hidden = !c.hasBus;
    }

    function updateBusNotice() {
      const c = CAMPUSES[campusSel.value];
      if (busNoticeWrap) busNoticeWrap.hidden = !c.hasBus;
      if (c.hasBus) {
        const contact = c.contact || {};
        const phoneHref = contact.phone
          ? "tel:" + contact.phone.replace(/\s+/g, "")
          : "";
        const rows = [
          contact.address ? contactRow(ICON_ADDRESS, contact.address) : "",
          contact.phone
            ? contactRow(
                ICON_PHONE,
                `<a href="${phoneHref}">${contact.phone}</a>`
              )
            : "",
          contact.email
            ? contactRow(
                ICON_EMAIL,
                `<a href="mailto:${contact.email}">${contact.email}</a>`
              )
            : "",
        ].join("");

        busNoticeText.innerHTML = `
          <p>${BUS_NOTE}</p>
          <div class="hoth-contact-block">${rows}</div>
        `;
      }
    }

    /* ---- dynamic builders ---- */
    function populateAgeGroups() {
      const c = CAMPUSES[campusSel.value];
      let keys = Object.keys(c.ageGroups);
      if (ALLOWED_AGE_GROUPS.length && campusSel.value === LOCKED_CAMPUS) {
        const allowed = keys.filter((k) => ALLOWED_AGE_GROUPS.includes(k));
        if (allowed.length) keys = allowed;
      }
      ageSel.innerHTML = "";
      keys.forEach((k) => {
        const o = document.createElement("option");
        o.value = k;
        o.textContent = c.ageGroups[k].label;
        ageSel.appendChild(o);
      });
      ageGroupField.hidden = keys.length <= 1;
      ageGroupHint.textContent = keys.includes("infant")
        ? "Infant enrolment (2–17 months) is offered exclusively at Balmoral Campus."
        : "";
    }

    function populateSchedules() {
      const c = CAMPUSES[campusSel.value];
      const age = c.ageGroups[ageSel.value]
        ? ageSel.value
        : Object.keys(c.ageGroups)[0];
      const group = c.ageGroups[age];
      schedSel.innerHTML = "";
      group.schedules.forEach((s) => {
        const o = document.createElement("option");
        o.value = s.id;
        o.textContent = s.label;
        schedSel.appendChild(o);
      });
    }

    function populateElectives() {
      const c = CAMPUSES[campusSel.value];
      const age = c.ageGroups[ageSel.value]
        ? ageSel.value
        : Object.keys(c.ageGroups)[0];
      const electives = c.ageGroups[age].electives || [];
      electivesList.innerHTML = "";

      const electivesGroup = electivesList.closest(".hoth-field-group");
      if (electivesGroup) electivesGroup.hidden = electives.length === 0;

      electives.forEach((e) => {
        const rowEl = document.createElement("div");
        rowEl.className = "hoth-elective";
        rowEl.innerHTML = `
          <input type="checkbox" data-price="${e.incl}" data-excl="${
          e.excl
        }" data-label="${e.label}" class="elective-chk" id="el-${e.id}">
          <span class="hoth-elective__label">
            <label for="el-${e.id}">${e.label}</label>
            ${
              e.note ? `<span class="hoth-elective__note">${e.note}</span>` : ""
            }
          </span>
          <span class="hoth-elective__price">${fmt(e.excl)}/mo</span>`;
        electivesList.appendChild(rowEl);
      });
    }

    function toggleMemberField() {
      const c = CAMPUSES[campusSel.value];
      memberField.hidden = !c.hasMemberDiscount;
      if (!c.hasMemberDiscount) hcMember.checked = false;
    }

    function toggleCitizenshipField() {
      const c = CAMPUSES[campusSel.value];
      citizenshipField.hidden = !c.hasSubsidy;
      if (!c.hasSubsidy) citizenshipSel.value = "foreigner";
    }

    function toggleSubsidyField() {
      const c = CAMPUSES[campusSel.value];
      const isCitizen = citizenshipSel.value === "citizen";
      subsidyField.hidden = !(c.hasSubsidy && isCitizen);
    }

    /* ---- subsidy ---- */
    function getSubsidyEntry() {
      const c = CAMPUSES[campusSel.value];
      const age = c.ageGroups[ageSel.value]
        ? ageSel.value
        : Object.keys(c.ageGroups)[0];
      const sched = getSelectedSchedule();
      const category = sched.subsidyCategory;
      const table = c.ageGroups[age].subsidy || {};
      return { category, entry: category ? table[category] : null };
    }

    function updateSubsidyHint() {
      const { category, entry } = getSubsidyEntry();
      if (!category || !entry) {
        subsidyHint.textContent =
          "No published subsidy schedule for this attendance option.";
      } else {
        const label = SUBSIDY_LABELS[category] || category;
        subsidyHint.textContent = `Subsidy tier: ${label} — ${fmt(
          entry.nonWorking
        )} (non‑working) / ${fmt(entry.working)} (working).`;
      }
    }

    /* ---- calculation ---- */
    function getSelectedSchedule() {
      const c = CAMPUSES[campusSel.value];
      const age = c.ageGroups[ageSel.value]
        ? ageSel.value
        : Object.keys(c.ageGroups)[0];
      const group = c.ageGroups[age];
      return (
        group.schedules.find((s) => s.id === schedSel.value) ||
        group.schedules[0]
      );
    }
    function getSelectedAgeGroupData() {
      const c = CAMPUSES[campusSel.value];
      const age = c.ageGroups[ageSel.value]
        ? ageSel.value
        : Object.keys(c.ageGroups)[0];
      return c.ageGroups[age];
    }
    function getSelectedElectives() {
      return [...document.querySelectorAll(".elective-chk:checked")].map(
        (chk) => ({
          label: chk.dataset.label,
          incl: parseFloat(chk.dataset.price),
          excl: parseFloat(chk.dataset.excl),
        })
      );
    }

    function rowHtml(label, amt, sub) {
      const subHtml = sub
        ? `<span class="hoth-elective__note" style="font-style:normal;">${sub}</span>`
        : "";
      return `<span>${label}${subHtml}</span><span>${fmt(amt)}</span>`;
    }

    function calculate() {
      const campus = CAMPUSES[campusSel.value];
      const sched = getSelectedSchedule();
      const electives = getSelectedElectives();
      const isMember = campus.hasMemberDiscount && hcMember.checked;
      const isCitizen = citizenshipSel.value === "citizen";

      updateSubsidyHint();
      const { entry: subsidyEntry } = getSubsidyEntry();
      const subsidyStatus = subsidyStatusSel.value;
      const subsidyAmt =
        isCitizen && subsidyEntry ? subsidyEntry[subsidyStatus] : 0;

      const hcDiscountAmt = isMember && sched.discount ? sched.discount : 0;
      const baseFeeExGst = sched.deposit - hcDiscountAmt;

      el("resCampus").textContent =
        (CAMPUS_LABEL && campusSel.value === LOCKED_CAMPUS
          ? CAMPUS_LABEL
          : campus.name) + (YEARS.length > 1 ? " · " + YEAR + " rates" : "");
      el("resSchedule").textContent = sched.label;
      el("baseFeeRow").innerHTML = rowHtml("Base fee", baseFeeExGst);
      el("baseFeeNote").hidden = hcDiscountAmt <= 0;

      const electiveRows = el("electiveRows");
      electiveRows.innerHTML = "";
      electives.forEach((e) => {
        const r = document.createElement("div");
        r.className = "hoth-summary__row";
        r.innerHTML = rowHtml(e.label, e.excl);
        electiveRows.appendChild(r);
      });
      electiveRows.hidden = electives.length === 0;
      const electivesTotal = electives.reduce((s, e) => s + e.excl, 0);

      const discountRow = el("discountRow");
      discountRow.hidden = true;

      const subsidyDiscountRow = el("subsidyDiscountRow");
      if (subsidyAmt > 0) {
        subsidyDiscountRow.hidden = false;
        const statusLabel =
          subsidyStatus === "working" ? "Working mother" : "Non-working mother";
        subsidyDiscountRow.innerHTML = rowHtml(
          `Childcare subsidy (${statusLabel})`,
          -subsidyAmt
        );
      } else {
        subsidyDiscountRow.hidden = true;
      }

      const discountsWrap = el("discountsWrap");
      discountsWrap.hidden = discountRow.hidden && subsidyDiscountRow.hidden;

      const grossSubtotal = baseFeeExGst + electivesTotal;
      const netSubtotal = Math.max(0, grossSubtotal - subsidyAmt);
      const monthlyGst = netSubtotal * GST_RATE;
      const monthlyTotal = netSubtotal + monthlyGst;

      el("monthlySubtotal").textContent = fmt(netSubtotal);
      el("monthlyGstRow").innerHTML = rowHtml(
        `GST (${(GST_RATE * 100).toFixed(0)}%)`,
        monthlyGst
      );
      el("monthlyGrandTotal").textContent = fmt(monthlyTotal);

      /* ---- First month enrolment: subtotal (incl. discounts) first, then GST once ---- */
      const onetimeRows = el("onetimeRows");
      const ageData = getSelectedAgeGroupData();
      const skipUniform = ageData && ageData.hasUniform === false;
      const uniformExclTotal = UNIFORM.exclPerSet * UNIFORM.qty;

      const exclItems = [
        { label: REGISTRATION.label, amt: REGISTRATION.excl },
        { label: INSURANCE.label, amt: INSURANCE.excl },
      ];
      if (!skipUniform) {
        exclItems.push({
          label: `${UNIFORM.label} (${UNIFORM.qty} sets)`,
          amt: uniformExclTotal,
        });
      }
      exclItems.push({
        label: `First month school fees — ${sched.label}`,
        amt: baseFeeExGst,
      });
      electives.forEach((e) => {
        exclItems.push({ label: `${e.label} (first month)`, amt: e.excl });
      });
      exclItems.push({
        label: "Deposit — equivalent to one month's school fees",
        amt: baseFeeExGst,
      });

      onetimeRows.innerHTML = "";
      exclItems.forEach((item) => {
        const r = document.createElement("div");
        r.className = "hoth-summary__row";
        r.innerHTML = rowHtml(item.label, item.amt);
        onetimeRows.appendChild(r);
      });

      const exclSubtotal = exclItems.reduce((s, i) => s + i.amt, 0);
      const enrolmentDiscountTotal = subsidyAmt;

      const enrolmentDiscountRow = el("enrolmentDiscountRow");
      if (enrolmentDiscountTotal > 0) {
        enrolmentDiscountRow.hidden = false;
        enrolmentDiscountRow.innerHTML = rowHtml(
          "Less: childcare subsidy (applied to month 1 fee)",
          -enrolmentDiscountTotal
        );
      } else {
        enrolmentDiscountRow.hidden = true;
      }

      const enrolmentNetSubtotal = Math.max(
        0,
        exclSubtotal - enrolmentDiscountTotal
      );
      const enrolmentGst = enrolmentNetSubtotal * GST_RATE;
      const enrolmentTotal = enrolmentNetSubtotal + enrolmentGst;

      el("enrolmentSubtotal").textContent = fmt(enrolmentNetSubtotal);
      el("gstRow").innerHTML = rowHtml(
        `GST (${(GST_RATE * 100).toFixed(0)}%)`,
        enrolmentGst
      );
      el("enrolmentGrandTotal").textContent = fmt(enrolmentTotal);
    }

    /* ---- events ---- */
    if (yearSel) {
      yearSel.addEventListener("change", () => {
        // remember current selections
        const prev = {
          campus: campusSel.value,
          age: ageSel.value,
          sched: schedSel.value,
          electives: [...document.querySelectorAll(".elective-chk:checked")].map(
            (c) => c.id
          ),
        };

        loadYear(yearSel.value);
        if (yearHint) yearHint.textContent = YEAR_NOTE;

        // rebuild the cascade, restoring each choice if it still exists
        populateCampuses();
        if (CAMPUSES[prev.campus]) campusSel.value = prev.campus;

        populateAgeGroups();
        if ([...ageSel.options].some((o) => o.value === prev.age))
          ageSel.value = prev.age;

        populateSchedules();
        if ([...schedSel.options].some((o) => o.value === prev.sched))
          schedSel.value = prev.sched;

        populateElectives();
        prev.electives.forEach((id) => {
          const chk = document.getElementById(id);
          if (chk) chk.checked = true;
        });

        toggleMemberField();
        toggleCitizenshipField();
        toggleSubsidyField();
        updateBusNotice();
        updateAdditionalServicesGroup();
        calculate();
      });
    }
    campusSel.addEventListener("change", () => {
      populateAgeGroups();
      populateSchedules();
      populateElectives();
      toggleMemberField();
      toggleCitizenshipField();
      toggleSubsidyField();
      updateBusNotice();
      updateAdditionalServicesGroup();
      calculate();
    });
    ageSel.addEventListener("change", () => {
      populateSchedules();
      populateElectives();
      calculate();
    });
    schedSel.addEventListener("change", () => {
      calculate();
    });
    hcMember.addEventListener("change", calculate);
    citizenshipSel.addEventListener("change", () => {
      toggleSubsidyField();
      calculate();
    });
    subsidyStatusSel.addEventListener("change", calculate);
    document.addEventListener("change", (e) => {
      if (e.target.classList.contains("elective-chk")) calculate();
    });

    /* ---- init ---- */
    populateYears();
    populateCampuses();
    campusSel.value = campusSel.options[0].value;
    populateAgeGroups();
    populateSchedules();
    populateElectives();
    toggleMemberField();
    toggleCitizenshipField();
    toggleSubsidyField();
    updateBusNotice();
    updateAdditionalServicesGroup();
    calculate();
  }
})();